// Resolves the *effective* default schema for an engine when a name is
// unqualified — i.e. the schema the object actually lives under, not a
// best-effort label. This matters wherever an unqualified name is persisted
// or exact-matched against real schema metadata (e.g. MetaInfo JSON-column
// declarations), where MySQL's "schema == database" and Oracle's
// "schema == user" must be honoured rather than approximated as `dbo`.
//
// Distinct from the navigational `defaultSchemaForEngine`
// (`features/navigational-features/go-to/externalNavigation.ts`), which is a
// Phase-1 single-arg approximation that intentionally tolerates `dbo` for
// MySQL/Oracle because OE-node matching folds case and is fuzzy. Do not
// conflate the two: use this one whenever the result is written or
// exact-compared.

import type { DatabaseEngineId } from '@/core/engines';
import { getConnectionById } from '@/features/connections/utils/connectionLookup';

export function resolveDefaultSchemaForEngine(
  engineId: DatabaseEngineId,
  database?: string,
  connectionId?: string,
): string {
  if (engineId === 'pgsql') return 'public';
  if (engineId === 'sqlite') return '';
  if (engineId === 'mysql') return database ?? 'mysql';
  if (engineId === 'oracle') {
    if (connectionId) {
      try {
        const { connection } = getConnectionById(connectionId);
        if (connection?.oracleInitialSchema) {
          return connection.oracleInitialSchema.toUpperCase();
        }
        if (connection?.login) return connection.login.toUpperCase();
      } catch {
        // connection lookup not available (e.g. in unit tests)
      }
    }
    // Oracle never lives under `dbo`; SYSTEM is the safe schema-shaped
    // fallback when the connection can't be resolved.
    return 'SYSTEM';
  }
  return 'dbo'; // mssql / mock
}
